import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  X, Loader2, Check, Ban, AlertTriangle, CalendarClock, ExternalLink, Phone,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { relabelBranch } from '../../lib/doctorSchedule.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'
import {
  leaveStatusMeta, toneClass, leaveDateLabel, leaveWindowLabel, leaveDayCount,
  conflictSummary, shiftLabel, fmtDay,
} from '../../lib/doctorLeave.js'

/**
 * Approve or reject one doctor's leave — and, first, show what it collides with.
 *
 * 🚨 THE CONFLICT PANEL IS WHY THIS DRAWER EXISTS. Approving leave NEVER writes
 * to `appointment` (owner's decision, 2026-08-20). The two facts a manager needs
 * before saying yes are:
 *
 *   1. does this doctor already have a shift rostered in that window, and
 *   2. **are there patients already booked into it**
 *
 * (2) is the one nobody else in the clinic will notice. A leave approved over a
 * day with four patients on the book is four phone calls that no longer get
 * made, and neither the roster nor the booking calendar will ever raise it. So
 * it is stated first, in its own colour, and ยกเลิกเวรวันนี้ด้วย is a separate
 * deliberate trip to the roster rather than a side effect of approving.
 */

const INPUT = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

export default function LeaveDecisionDrawer({ id, onClose, onDecided }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(null)   // 'approved' | 'rejected' | null
  const [branches, setBranches] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiGet(`/api/hr/doctor-leave/${id}`)
      setData(r)
      setNote(r?.leave?.decision_note || '')
      setErr('')
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดคำขอลาไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    let alive = true
    cachedGet('/api/branches')
      .then((b) => { if (alive) setBranches(Array.isArray(b) ? b : (b?.items || [])) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const leave = data?.leave
  const doctor = data?.doctor
  const conflicts = useMemo(() => conflictSummary(data?.conflicts), [data])
  const branchName = useCallback((bid) => {
    const b = branches.find((x) => Number(x.id) === Number(bid))
    return b ? relabelBranch(b.name) : `สาขา #${bid}`
  }, [branches])

  async function decide(status) {
    setConfirm(null)
    setBusy(true)
    try {
      await patchJson(`/api/hr/doctor-leave/${id}`, { status, decision_note: note.trim() })
      onDecided?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
      setBusy(false)
    }
  }

  const meta = leaveStatusMeta(leave?.status)
  const pending = leave?.status === 'pending'
  const label = doctorCode(doctor) || doctorFullName(doctor) || `หมอ #${leave?.doctor_id ?? '—'}`

  return (
    <Shell title="คำขอลา" onClose={onClose}>
      {loading ? (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-500 py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> กำลังโหลด…
        </div>
      ) : err && !leave ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[15px] font-semibold text-slate-900">{label}</span>
              <span className={`rounded-md px-1.5 py-0.5 text-[11.5px] ring-1 ${toneClass(meta.tone)}`}>{meta.label}</span>
              {doctor?.phone && (
                <a href={`tel:${doctor.phone}`} className="ml-auto inline-flex items-center gap-1 text-[12.5px] text-slate-400 hover:text-brand-600 transition-colors">
                  <Phone size={12} strokeWidth={1.75} /> {doctor.phone}
                </a>
              )}
            </div>
            <p className="text-[13.5px] text-slate-700">
              {leaveDateLabel(leave)}
              <span className="text-slate-400"> · </span>
              {leaveWindowLabel(leave)}
              {leave?.type === 'full_day' && leaveDayCount(leave) > 1 && (
                <span className="text-slate-400"> · {leaveDayCount(leave)} วัน</span>
              )}
            </p>
            {leave?.reason && <p className="text-[13px] text-slate-500 mt-1.5">เหตุผล: {leave.reason}</p>}
          </div>

          {/* Conflicts. Patients first — it is the fact with a consequence. */}
          {data?.conflicts ? (
            <div className="space-y-2">
              {conflicts.patientCount > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 flex items-start gap-2">
                  <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-rose-600" />
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-rose-900">{conflicts.patientNote}</p>
                    <p className="text-[12.5px] text-rose-800/80 mt-0.5">
                      อนุมัติแล้วนัดของคนไข้จะยังอยู่ในระบบ — ระบบไม่ลบให้อัตโนมัติ
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5 text-[12.5px] text-slate-500">
                  <CalendarClock size={13} strokeWidth={1.75} />
                  {conflicts.shiftNote}
                </div>
                {conflicts.shiftCount > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {conflicts.shifts.map((s) => (
                      <li key={s.id} className="px-3 py-2 flex items-center gap-2 text-[13px] text-slate-700">
                        <span className="shrink-0 text-slate-500">{fmtDay(s.date)}</span>
                        <span className="truncate">{branchName(s.branch_id)}</span>
                        <span className="ml-auto shrink-0 text-slate-500">{shiftLabel(s)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {conflicts.shiftCount > 0 && (
                  <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                    {/* Deliberately a LINK to the roster, not a button that acts
                        here. Removing a shift is the roster's own operation,
                        with its own gate and its own realtime emit — and it must
                        be a decision, not a side effect of approving leave. */}
                    <Link
                      to={`/manager/schedule/doctor?branch=${conflicts.shifts[0]?.branch_id || ''}&date=${String(conflicts.shifts[0]?.date || '').slice(0, 10)}`}
                      className="inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-700 hover:text-brand-800"
                    >
                      <ExternalLink size={12} strokeWidth={2} />
                      เปิดตารางเวรเพื่อยกเลิกเวรวันนี้
                    </Link>
                  </div>
                )}
                {conflicts.truncated && (
                  <div className="px-3 py-1.5 text-[11.5px] text-amber-700 bg-amber-50 border-t border-amber-100">
                    แสดงไม่ครบ — ช่วงวันที่ลายาวเกิน 500 รายการ
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-[13px] font-medium text-slate-600 mb-1.5">หมายเหตุถึงคุณหมอ</label>
            <textarea
              className={`${INPUT} min-h-[72px] resize-y`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ไม่บังคับ — เช่น อนุมัติแล้ว ฝากแจ้งเคาน์เตอร์ด้วย"
              disabled={!pending || busy}
            />
          </div>

          {err && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

          {pending ? (
            <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
              <button
                disabled={busy}
                onClick={() => setConfirm('approved')}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-[13.5px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} strokeWidth={2} />} อนุมัติ
              </button>
              <button
                disabled={busy}
                onClick={() => setConfirm('rejected')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-[13.5px] text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <Ban size={14} strokeWidth={1.75} /> ไม่อนุมัติ
              </button>
            </div>
          ) : (
            <p className="text-[12.5px] text-slate-400 pt-1 border-t border-slate-100">
              คำขอนี้พิจารณาแล้ว — เปลี่ยนสถานะได้จากปุ่มด้านล่าง
              {' '}
              <button
                disabled={busy}
                onClick={() => setConfirm('pending')}
                className="text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
              >
                ย้อนกลับเป็นรอยืนยัน
              </button>
            </p>
          )}
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={
            confirm === 'approved' ? 'อนุมัติคำขอลานี้?'
              : confirm === 'rejected' ? 'ไม่อนุมัติคำขอลานี้?'
              : 'ย้อนกลับเป็นรอยืนยัน?'
          }
          // The message carries the consequence, not a generic "are you sure".
          // On an approval with patients booked, that consequence is the point.
          message={
            confirm === 'approved'
              ? `${label} · ${leaveDateLabel(leave)}${
                  conflicts.patientCount > 0
                    ? ` — ⚠️ มีคนไข้จองไว้ ${conflicts.patientCount} ราย นัดเหล่านี้จะยังอยู่ในระบบ ต้องติดต่อเลื่อนเอง`
                    : ''
                }${
                  conflicts.shiftCount > 0 ? ` · เวรในตารางจะไม่ถูกลบให้อัตโนมัติ` : ''
                }`
              : confirm === 'rejected'
                ? `${label} · ${leaveDateLabel(leave)} — คุณหมอจะเห็นสถานะ “ไม่อนุมัติ” พร้อมหมายเหตุที่เขียนไว้`
                : `${label} · ${leaveDateLabel(leave)} — กลับไปเป็นคำขอที่ยังไม่ตัดสิน`
          }
          confirmLabel={confirm === 'approved' ? 'อนุมัติ' : confirm === 'rejected' ? 'ไม่อนุมัติ' : 'ย้อนกลับ'}
          tone={confirm === 'approved' && conflicts.patientCount > 0 ? 'danger' : 'default'}
          onConfirm={() => decide(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </Shell>
  )
}

function Shell({ title, children, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-[560px] bg-white shadow-xl flex flex-col">
        <header className="flex items-center gap-3 px-5 h-14 border-b border-slate-200 shrink-0">
          <h2 className="text-[15px] font-semibold text-slate-900 truncate">{title}</h2>
          <button onClick={onClose} className="ml-auto p-1.5 -mr-1.5 text-slate-400 hover:text-slate-700 transition-colors">
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </aside>
    </div>
  )
}
